import React, { useEffect, useState } from 'react';
import { SetupPageHeader } from '../../../../components/SetupPageHeader';
import { Card } from '../../../../components/Card';
import { BackArrow } from '../../../../components/BackArrow';
import { PageTitle } from '../../../../components/PageTitle';
import { NameInput } from '../../../survey_engine/SurveyTemplates/NameInput';
import { InterviewParticipants } from '../../Interviews/ScheduleInterviewPage/InterviewDetails/InterviewParticipants';
import { InterviewerParticipant } from '../../../../entities/applicant_tracking/InterviewerParticipant';
import { User } from '../../../../entities/User';
import {
  DEFAULT_DURATION_IN_MINUTES,
  InterviewDuration,
} from '../../Interviews/ScheduleInterviewPage/InterviewDetails/InterviewDuration';
import { InterviewMeetingProvider } from '../../Interviews/ScheduleInterviewPage/InterviewDetails/InterviewMeetingProvider';
import { VcsProvider } from '../../../../enums/IntegrationProvider/VcsProvider';
import classNames from 'classnames';
import { TimezoneSelect } from '../../../../components/TimezoneSelect';
import {
  DateRangeSelect,
  DEFAULT_DATE_RANGE,
} from '../../../../components/DateRangeSelect';
import {
  DEFAULT_SCHEDULE_FROM_HOURS,
  ScheduleFromSelect,
} from '../../Interviews/ScheduleInterviewPage/ScheduleFromSelect';
import {
  mapIntToScheduleFrom,
  ScheduleFromType,
} from '../../Interviews/ScheduleInterviewPage/ScheduleFromSelect/ScheduleFromTypes';
import { Col, Row } from 'reactstrap';
import {
  getAvailableMeetingProviders,
  getDefaultMeetingProvider,
} from '../../../../utils/applicant_tracking/interviews/interviews';
import { InterviewTemplate } from '../../../../entities/applicant_tracking/InterviewTemplate';
import { ActionType } from '../../../../enums/ActionType';
import { IconSpan } from '../../../../components/IconSpan';

const BACK_URL = '/organization_settings/interview_templates';
const NAME_MAXIMUM_CHARACTERS = 32;

interface PropTypes {
  meetingOrganizer: User;
  availableParticipants: User[];
  interviewTemplate?: InterviewTemplate;
  actionType: ActionType;
}

function getParticipants(
  meetingOrganizer: User,
  interviewTemplate?: InterviewTemplate,
): InterviewerParticipant[] {
  if (!interviewTemplate) {
    return [
      {
        user: meetingOrganizer,
        isMeetingOrganizer: true,
        ignoreCalendar: false,
      },
    ];
  }

  return interviewTemplate.participants.map((user) => ({
    user: user,
    isMeetingOrganizer: user.id === meetingOrganizer.id,
    ignoreCalendar: false,
  }));
}

function getMeetingProvider(
  meetingOrganizer: User,
  interviewTemplate?: InterviewTemplate,
): VcsProvider {
  if (!interviewTemplate) {
    return getDefaultMeetingProvider(meetingOrganizer);
  }

  return interviewTemplate.meetingProvider;
}

export default function InterviewTemplatePage(props: PropTypes) {
  const [actionType, setActionType] = useState<ActionType>(props.actionType);
  const [name, setName] = useState<string>(props.interviewTemplate?.name || '');
  const [participants, setParticipants] = useState<InterviewerParticipant[]>(
    getParticipants(props.meetingOrganizer, props.interviewTemplate),
  );
  const [durationMinutes, setDurationMinutes] = useState<number>(
    props.interviewTemplate?.meetingDuration || DEFAULT_DURATION_IN_MINUTES,
  );
  const [meetingProvider, setMeetingProvider] = useState<VcsProvider>(
    getMeetingProvider(props.meetingOrganizer, props.interviewTemplate),
  );
  const [availableMeetingProviders, setAvailableMeetingProviders] = useState<
    VcsProvider[]
  >(getAvailableMeetingProviders(props.meetingOrganizer));
  const [timezone, setTimezone] = useState<string>(
    props.interviewTemplate?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [dateRange, setDateRange] = useState<number>(
    props.interviewTemplate?.dateRange || DEFAULT_DATE_RANGE,
  );
  const [scheduleFrom, setScheduleFrom] = useState<ScheduleFromType>(
    mapIntToScheduleFrom(
      props.interviewTemplate?.scheduleFromHours || DEFAULT_SCHEDULE_FROM_HOURS,
    ),
  );
  const [isSaveEnabled, setSaveEnabled] = useState<boolean>(false);

  const onChangingMeetingOrganizer = (participant: InterviewerParticipant) => {
    setMeetingProvider(getDefaultMeetingProvider(participant.user));
    setAvailableMeetingProviders(
      getAvailableMeetingProviders(participant.user),
    );
  };

  const onEdit = () => {
    setActionType(ActionType.Edit);
  };

  const onCancel = () => {
    if (actionType === ActionType.Edit) {
      window.location.reload();
    } else {
      window.location.href = BACK_URL;
    }
  };

  //TODO: Follow-up PR will connect to the backend
  const onSave = () => console.log('Saving');

  useEffect(() => {
    setSaveEnabled(name.length > 0 && name.length < NAME_MAXIMUM_CHARACTERS);
  }, [name]);

  const isReadOnly = actionType === ActionType.Show;

  return (
    <div className='container'>
      <Title name={props.interviewTemplate?.name} />
      <Card>
        <CardHeader
          actionType={actionType}
          onEdit={onEdit}
          isSaveEnabled={isSaveEnabled}
          onSave={onSave}
          onCancel={onCancel}
        />
        <div className={'w-50'}>
          <div className='d-flex flex-column fs-5 pt-2 pb-1 gap-4'>
            <NameInput
              name={name}
              onName={setName}
              placeholder='Insert interview template name'
              maximumCharacters={NAME_MAXIMUM_CHARACTERS}
              readOnly={isReadOnly}
            />
          </div>
          <InterviewParticipants
            participants={participants}
            setParticipants={setParticipants}
            availableParticipants={props.availableParticipants}
            candidateView={isReadOnly}
            allowChangingMeetingOrganizer={true}
            allowIgnoreParticipantCalendar={false}
            disableAlert={true}
            isRequired={true}
            onChangingMeetingOrganizer={onChangingMeetingOrganizer}
            titleClassName={'text-primary-dark fw-semibold'}
          />
          <Row className={'mt-3h'}>
            <Col sm={6}>
              <InterviewDuration
                durationMinutes={durationMinutes}
                setDurationMinutes={setDurationMinutes}
                candidateView={false}
                selectSize={'md'}
                readOnly={isReadOnly}
                titleClassName={'text-primary-dark fw-semibold'}
              />
            </Col>
            <Col sm={6}>
              <InterviewMeetingProvider
                meetingProvider={meetingProvider}
                setMeetingProvider={setMeetingProvider}
                meetingProviders={availableMeetingProviders}
                candidateView={false}
                selectSize={'md'}
                readOnly={isReadOnly}
                titleClassName={'text-primary-dark fw-semibold'}
              />
            </Col>
          </Row>
          <Row className={'mt-3h'}>
            <Col sm={6}>
              <ScheduleFromSelect
                scheduleFrom={scheduleFrom}
                setScheduleFrom={setScheduleFrom}
                titleClassName={'text-primary-dark fw-semibold fs-5'}
                selectClassName={classNames('mt-2')}
                selectSize={'md'}
                toolTipText={
                  'This will allow scheduling from the moment the link is shared.'
                }
                readOnly={isReadOnly}
              />
            </Col>
            <Col sm={6}>
              <DateRangeSelect
                dateRange={dateRange}
                setDateRange={setDateRange}
                titleClassName={'text-primary-dark fw-semibold fs-5'}
                selectClassName={classNames('mt-2')}
                selectSize={'md'}
                readOnly={isReadOnly}
              />
            </Col>
          </Row>
          <Row className={'mt-3h'}>
            <Col sm={6}>
              <TimezoneSelect
                timezone={timezone}
                setTimezone={setTimezone}
                titleClassName={'text-primary-dark fw-semibold fs-5'}
                selectClassName={classNames('mt-2')}
                selectSize={'md'}
                readOnly={isReadOnly}
              />
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
}

function Title(props: { name?: string }) {
  return (
    <div className='d-flex align-items-center mb-4'>
      <BackArrow url={BACK_URL} />
      <PageTitle text={props.name ?? 'Add Interview Template'} />
    </div>
  );
}

function CardHeader(props: {
  actionType: ActionType;
  onEdit: () => void;
  isSaveEnabled: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  if (props.actionType === ActionType.Show) {
    return <EditCardHeader onEdit={props.onEdit} />;
  }

  return (
    <SaveCardHeader
      isSaveEnabled={props.isSaveEnabled}
      onSave={props.onSave}
      onCancel={props.onCancel}
    />
  );
}

function EditCardHeader(props: { onEdit: () => void }) {
  return (
    <SetupPageHeader
      title={'Interview Template'}
      firstItems={[]}
      intermediateItems={[
        {
          title: <IconSpan spanText='Edit' icon={{ name: 'bi-pencil' }} />,
          type: 'Navigate',
          disabled: false,
          action: props.onEdit,
        },
      ]}
    />
  );
}

function SaveCardHeader(props: {
  isSaveEnabled: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <SetupPageHeader
      title={'Interview Template'}
      firstItems={[]}
      intermediateItems={[
        {
          title: 'Cancel',
          type: 'Cancel',
          action: props.onCancel,
        },
        {
          title: 'Save',
          type: 'Save',
          disabled: !props.isSaveEnabled,
          action: props.onSave,
        },
      ]}
    />
  );
}
